import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { Select2 } from '@/components/forms/Select2';
import { LineChart } from '@/components/charts/LineChart';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { http } from '@/api/client/httpClient';
import { formatNumber } from '@/utils/format';
import { sigmaApi } from '@/api/services/sigma';
import { SigmaChart } from './SigmaChart';
import { useSigmaLive, type SigmaSubscribeInput } from './useSigmaLive';
import {
  SigmaFilterBar,
  defaultSigmaFilters,
  toSigmaInput,
  intervalLabel,
  dateFiltersEmpty,
  multipleFiltersChosen,
  type SigmaFilterState,
} from './SigmaFilterBar';
import { SigmaPermissions as P } from './permissions';
import type { SigmaDataPointDto } from '@/api/types/sigma';

const num = (v: number | null | undefined, d = 2) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, d));

// Trailing blank points after the live marker so it sits inset from the right
// edge (mirrors LIVE_OFFSET in sigma-chart.js).
const LIVE_OFFSET = 3;

function fmtLocalNow(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// Mirrors computeLastStaticRefreshAt in sigma-index.js.
function computeLastStaticRefreshAt(startDate: string, intervalMinutes: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(String(startDate).replace(' ', 'T'));
  let start = m ? new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)) : new Date();
  const now = new Date();
  if (!m || !intervalMinutes || intervalMinutes <= 0) start = now;
  const stepMs = intervalMinutes * 60000;
  let t = start.getTime();
  while (t < now.getTime()) t += stepMs;
  t = t - stepMs + 5000;
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export function SigmaPage() {
  const toast = useToast();
  const can = usePermission();
  const canConsolidated = can(P.consolidatedView);

  const [draft, setDraft] = useState<SigmaFilterState>(defaultSigmaFilters);
  const [applied, setApplied] = useState<SigmaFilterState>(draft);
  const [chartData, setChartData] = useState(0); // 0 Profit, 1 σ Factor, 2 Equity
  const [showDots, setShowDots] = useState(false);
  const [livePoint, setLivePoint] = useState<number | null>(null);
  // Bumped per Fetch (only for live / today data) so re-applying those filters forces a
  // fresh request; static historical results stay cached. react-query keys by value.
  const [fetchNonce, setFetchNonce] = useState(0);
  // Brief Fetch-button feedback even on a cache hit (no network), so the click registers.
  const [fetchPulse, setFetchPulse] = useState(false);

  const liveActive = applied.live && applied.interval < 1440;

  const query = useQuery({
    queryKey: ['sigma', applied, chartData, fetchNonce],
    queryFn: () => sigmaApi.getSigmaData(toSigmaInput(applied, chartData)),
  });
  const result = query.data;

  // Surface backend error message (errorMsg) once per result.
  const errorMsg = result?.errorMsg;
  const lastErrRef = useRef<string | undefined>(undefined);
  if (errorMsg && errorMsg !== lastErrRef.current) {
    lastErrRef.current = errorMsg;
    toast.error(errorMsg);
  } else if (!errorMsg) {
    lastErrRef.current = undefined;
  }

  const validate = (f: SigmaFilterState, silentEmpty: boolean): boolean => {
    if (dateFiltersEmpty(f)) {
      if (!silentEmpty) toast.error('Dates Cannot Be Empty');
      return false;
    }
    if (multipleFiltersChosen(f)) {
      toast.error('Cannot apply multiple filters at once');
      return false;
    }
    return true;
  };

  const fetch = () => {
    if (!validate(draft, false)) return;
    setLivePoint(null);
    setApplied({ ...draft });
    const endIsToday = new Date(draft.endDate).toDateString() === new Date().toDateString();
    if (draft.live || endIsToday) setFetchNonce((n) => n + 1);
    setFetchPulse(true);
    window.setTimeout(() => setFetchPulse(false), 450);
  };

  async function extract() {
    if (!validate(draft, false)) return;
    try {
      const payload = { ...toSigmaInput(draft, chartData), highlight: draft.highlight ?? null };
      const res = await http.post(sigmaApi.exportToExcelUrl, payload, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Sigma.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export Sigma data.');
    }
  }

  // ----- Dynamic labels (updateTitles) -----
  const bySymbol = applied.symbols.length > 0;
  const profitWord = bySymbol ? 'PnL' : 'Profit';
  const profitLabel = applied.cumulativeProfit ? `Cumulative ${profitWord}` : profitWord;
  const equityLabel = bySymbol ? 'Unrealized' : 'Equity';
  const netInOutLabel = bySymbol ? 'Realized' : 'Net In/Out';
  const avgLabel = bySymbol ? 'Average PnL' : 'Average Profit';
  const chartDataOptions = [
    { value: 0, label: profitLabel },
    { value: 1, label: 'σ Factor' },
    { value: 2, label: equityLabel },
  ];
  const chartTitle = chartDataOptions.find((o) => o.value === chartData)?.label ?? profitLabel;

  // ----- Summary -----
  const profitRows = (result?.tableData ?? []).filter((p) => p.profit != null && !Number.isNaN(p.profit));
  const avgProfit = profitRows.length ? profitRows.reduce((a, p) => a + (p.profit ?? 0), 0) / profitRows.length : 0;
  const who = applied.uniqueId ? applied.uniqueId : applied.login ? String(applied.login) : 'All';

  // ----- Chart data -----
  const chart = useMemo(() => {
    const pts = (result?.tableData ?? []).filter((p) => p.ignoreDataPoint === false);
    const labels = pts.map((p) => (p.date || '').replace('T', ' '));
    const values = pts.map((p) => (chartData === 1 ? p.sigmaFactor ?? null : chartData === 2 ? p.equity ?? null : p.profit ?? null));
    const sigmaFactors = pts.map((p) => p.sigmaFactor ?? null);
    let livePointIndex = -1;
    if (livePoint != null) {
      labels.push(fmtLocalNow().replace('T', ' '));
      values.push(livePoint);
      sigmaFactors.push(null);
      livePointIndex = values.length - 1;
      for (let i = 0; i < LIVE_OFFSET; i++) {
        labels.push('');
        values.push(null);
        sigmaFactors.push(null);
      }
    }
    return { labels, values, sigmaFactors, livePointIndex };
  }, [result, chartData, livePoint]);

  // ----- Live -----
  const getLiveInput = useCallback((): SigmaSubscribeInput => {
    const interval = applied.interval || 1;
    return {
      filters: toSigmaInput(applied, chartData),
      sigma: result?.sigma ?? 0,
      prevEquity: result?.lastEquity ?? 0,
      prevCumulativeProfit: result?.lastCumulativeProfit ?? 0,
      lastStaticRefreshAt: computeLastStaticRefreshAt(toSigmaInput(applied, chartData).startDate ?? '', interval),
    };
  }, [applied, chartData, result]);

  const refreshInFlight = useRef(false);
  const live = useSigmaLive({
    enabled: liveActive && !errorMsg,
    getInput: getLiveInput,
    onUpdate: (payload) => {
      if (payload.error) {
        toast.error(payload.error);
        return;
      }
      if (payload.value != null) setLivePoint(payload.value);
      if (payload.needsStaticRefresh && !refreshInFlight.current) {
        refreshInFlight.current = true;
        setLivePoint(null);
        void query.refetch().finally(() => {
          refreshInFlight.current = false;
          live.resubscribe();
        });
      }
    },
  });

  const columns: DataGridColumn<SigmaDataPointDto>[] = [
    { key: 'date', title: 'Date', className: 'text-start', value: (r) => r.date, cell: (r) => (r.date || '').replace('T', ' ') },
    { key: 'profit', title: profitLabel, className: 'text-end', value: (r) => r.profit ?? null, cell: (r) => num(r.profit) },
    { key: 'equity', title: equityLabel, className: 'text-end', value: (r) => r.equity ?? null, cell: (r) => num(r.equity) },
    { key: 'netInOut', title: netInOutLabel, className: 'text-end', value: (r) => r.netInOut ?? null, cell: (r) => num(r.netInOut) },
    {
      key: 'sigmaFactor',
      title: 'σ Factor',
      className: 'text-end',
      value: (r) => r.sigmaFactor ?? null,
      cell: (r) => {
        const d = r.sigmaFactor;
        const h = applied.highlight;
        const display = num(d);
        if (d != null && h != null && !Number.isNaN(h)) {
          if (h > 0 && d >= h) return <span>{display} <i className="fa fa-flag" style={{ color: 'red' }} /></span>;
          if (h < 0 && d <= h) return <span>{display} <i className="fa fa-flag" style={{ color: 'blue' }} /></span>;
        }
        return display;
      },
    },
  ];

  return (
    <div className="sigma-page">
      <PageHeader title="Sigma" breadcrumb={['Home', 'Sigma']} />

      <SigmaFilterBar draft={draft} setDraft={setDraft} onFetch={fetch} onExtract={extract} canConsolidated={canConsolidated} fetching={query.isFetching || fetchPulse} />

      <div className="row">
        <div className="col-12">
          <div className="card custom-card">
            <div className="card-header d-flex align-items-center">
              <h6 className="card-title mb-0 me-auto">{chartTitle}</h6>
              <div className="form-check form-switch me-3 mb-0">
                <input className="form-check-input" type="checkbox" role="switch" id="sg-show-dots" checked={showDots} onChange={(e) => setShowDots(e.target.checked)} />
                <label className="form-check-label small" htmlFor="sg-show-dots">Show Dots</label>
              </div>
              <div style={{ minWidth: 160 }}>
                <Select2 searchable={false} allowClear={false} value={String(chartData)} options={chartDataOptions} onChange={(v) => setChartData(Number(v) || 0)} />
              </div>
            </div>
            <div className="card-body">
              {chart.values.length ? (
                <SigmaChart labels={chart.labels} values={chart.values} sigmaFactors={chart.sigmaFactors} highlight={applied.highlight} showDots={showDots} livePointIndex={chart.livePointIndex} reportingCurrency={result?.reportingCurrency ?? ''} chartData={chartData} />
              ) : (
                <LineChart labels={[]} series={[]} height={320} emptyHint="No data" />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-3 mb-3">
          <div className="card custom-card h-100">
            <div className="card-header"><h6 className="card-title mb-0">Summary</h6></div>
            <div className="card-body">
              <SummaryRow label="Standard Deviation" value={num(result?.sigma)} />
              <SummaryRow label={avgLabel} value={num(avgProfit)} />
              <SummaryRow label="Data Points" value={String(profitRows.length)} />
              <SummaryRow label={applied.byUniqueId ? 'Unique Id' : 'Login'} value={who} />
              <SummaryRow label="Time Period" value={intervalLabel(applied.interval)} />
            </div>
          </div>
        </div>

        <div className="col-lg-9 mb-3">
          <div className="card custom-card h-100">
            <div className="card-body">
              <DataGrid
                columns={columns}
                data={result?.tableData ?? []}
                rowKey={(r) => r.date}
                pageSize={5}
                pageSizeOptions={[5, 10, 25, 50]}
                columnVisibilityToggle
                emptyMessage="No data."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="d-flex mb-2">
      <div className="fw-bold me-auto">{label}</div>
      <div>{value}</div>
    </div>
  );
}
